import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import TagIcon from "@redotech/redo-icons/tag-01.svg";
import {
  CustomerTagProvider,
  CustomerTagRecord,
  CustomerTagSource,
} from "@redotech/redo-model/customer-tags/customer-tag-record";
import { Provider } from "@redotech/redo-model/order";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { memo, useMemo, useState } from "react";
import { RedoMerchantClientContext } from "./client/context";
import { postCustomerTag } from "./client/customer-tags";
import { useTeamProviders } from "./hooks/use-team-providers";
import { TeamContext } from "./team";

import CommentsoldIcon from "@redotech/redo-icons/commentsold-logo.svg";
import { CustomerTagWithId } from "@redotech/redo-model/customer";
import { RedoBadgeProps } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import ShopifyIcon from "@redotech/redo-web/shopify/bag-logo.svg";
import { assertNever } from "@redotech/util/type";
import { JSXElementConstructor } from "react";

export function getIconForCustomerTagProvider(
  provider?: CustomerTagProvider | null,
): JSXElementConstructor<any> | null {
  switch (provider) {
    case CustomerTagProvider.REDO:
      return null;
    case CustomerTagProvider.COMMENTSOLD:
      return CommentsoldIcon;
    case CustomerTagProvider.SHOPIFY:
    case null:
    case undefined:
      return ShopifyIcon;
    default:
      assertNever(provider);
  }
}

export function getBadgePropsForCustomerTag(
  customerTag: Pick<CustomerTagWithId, "name" | "provider">,
): Omit<RedoBadgeProps, "size"> {
  const Icon = getIconForCustomerTagProvider(customerTag.provider);
  return {
    text: customerTag.name,
    iconTrailing: Icon ? { type: "icon", Icon, size: "sm" } : undefined,
  };
}

export const CreateCustomerTagModal = memo(function CreateTagModal({
  open,
  onClose,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (newTag: CustomerTagRecord) => Promise<void>;
}) {
  const merchantClient = useRequiredContext(RedoMerchantClientContext);
  const team = useRequiredContext(TeamContext);
  const [newTagName, setNewTagName] = useState("");
  const [newTagProvider, setNewTagProvider] =
    useState<CustomerTagProvider | null>(null);

  const { data: teamProviders } = useTeamProviders();
  const providerOptions: TextRedoListItem<CustomerTagProvider>[] = useMemo(
    () => [
      {
        id: CustomerTagProvider.REDO,
        text: "Redo",
        type: "text" as const,
        value: CustomerTagProvider.REDO,
      },
      ...(teamProviders?.available?.includes(Provider.SHOPIFY)
        ? [
            {
              id: CustomerTagProvider.SHOPIFY,
              text: "Shopify",
              type: "text" as const,
              value: CustomerTagProvider.SHOPIFY,
            },
          ]
        : []),
      ...(teamProviders?.available?.includes(Provider.COMMENTSOLD)
        ? [
            {
              id: CustomerTagProvider.COMMENTSOLD,
              text: "Commentsold",
              type: "text" as const,
              value: CustomerTagProvider.COMMENTSOLD,
            },
          ]
        : []),
    ],
    [teamProviders],
  );

  const handleClose = useHandler(() => {
    setNewTagName("");
    setNewTagProvider(null);
    onClose();
  });

  const handleCreateTag = useHandler(async () => {
    if (!newTagName.trim() || !newTagProvider) return;

    const createdTag = await postCustomerTag(
      merchantClient,
      {
        teamId: team._id,
        name: newTagName.trim(),
        pillTheme: PillTheme.SOLID_LIGHT_BLUE,
        source: CustomerTagSource.REDO_MARKETING,
        provider: newTagProvider,
      },
      () => {},
    );

    await onCreate(createdTag);
    handleClose();
  });

  return (
    <>
      {open && (
        <RedoModal
          onModalCloseRequested={handleClose}
          primaryButton={{
            onClick: handleCreateTag,
            text: "Create tag",
            disabled: !newTagName.trim() || !newTagProvider,
          }}
          secondaryButton={{ onClick: handleClose, text: "Cancel" }}
          title="Create new customer tag"
          TitleIcon={TagIcon}
        >
          <Flex dir="column" gap="lg">
            <LabeledInput label="Tag name">
              <RedoTextInput
                autoFocus
                placeholder="Enter tag name"
                setValue={setNewTagName}
                value={newTagName}
              />
            </LabeledInput>
            <LabeledInput
              description="Select an e-commerce provider to sync the tag with. If Redo is selected, the tag will exist only in Redo."
              label="E-commerce provider"
            >
              <RedoSingleSelectDropdownInput
                options={providerOptions}
                optionSelected={({ item }) => setNewTagProvider(item.value)}
                selectedItem={providerOptions.find(
                  (option) => option.value === newTagProvider,
                )}
              />
            </LabeledInput>
          </Flex>
        </RedoModal>
      )}
    </>
  );
});
